import SEO from "@/components/SEO";
import FAQAccordion from "@/components/FAQAccordion";
import { RelatedLinks } from "@/pages/ServicePage";
import { PageHero, SectionHead, CTABand, Reveal } from "@/components/Ui";
import { CheckCircle2, AlertTriangle } from "lucide-react";

const prestations = [
  "Création d'ouvertures dans un mur porteur",
  "Ouvertures pour portes et fenêtres",
  "Percements pour baies vitrées",
  "Agrandissement d'ouvertures existantes",
  "Pose de linteaux et poutres selon le projet",
  "Renforcement et reprise de structures",
];

const faq = [
  { q: "Peut-on ouvrir n'importe quel mur porteur ?", a: "Chaque ouverture nécessite une étude adaptée au bâtiment : nature du mur, charges reprises, hauteur disponible. Nous analysons la configuration existante avant toute intervention." },
  { q: "Quels renforts sont mis en place ?", a: "Selon le chantier : linteau béton, poutre métallique ou jambages. La solution est étudiée au cas par cas, jamais de manière générique." },
  { q: "Faut-il un bureau d'études ?", a: "Pour les configurations complexes ou les grandes portées, une étude structurelle peut être nécessaire : nous vous orientons en toute transparence avant le démarrage." },
];

export default function OuvertureMurPorteur() {
  return (
    <>
      <SEO
        title="Ouverture de mur porteur à Bernay et dans l'Eure | DMZ Normandie"
        description="Ouverture de mur porteur à Bernay : création de portes, fenêtres et baies vitrées, linteaux et renforcement étudiés selon votre bâtiment. Devis gratuit."
        path="/ouverture-mur-porteur"
      />
      <PageHero
        title="Ouverture de mur porteur à Bernay et dans l'Eure"
        subtitle="Créer une ouverture dans un mur porteur demande une étude adaptée au bâtiment et une mise en œuvre conforme aux contraintes techniques du chantier. DMZ Normandie réalise vos percements avec un chantier sécurisé et maîtrisé."
      />

      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14">
          <Reveal>
            <SectionHead overline="Nos interventions" title="Ouvrir, agrandir, sécuriser" />
            <div className="space-y-4 -mt-6">
              {prestations.map((p) => (
                <p key={p} className="flex items-start gap-3 text-zinc-300 font-light">
                  <CheckCircle2 size={18} className="text-[#C4A484] mt-0.5 shrink-0" /> {p}
                </p>
              ))}
            </div>
            <p className="mt-8 text-sm font-light text-zinc-400 leading-relaxed border-l-2 border-[#C4A484] pl-5">
              Besoin de créer une ouverture ? Contactez DMZ Normandie pour étudier votre projet.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="space-y-6">
            <div className="card-dark p-8">
              <h2 className="font-serif-display font-light text-2xl md:text-3xl text-[#F5F5F0] mb-4">Un travail qui ne s'improvise pas</h2>
              <p className="text-sm font-light text-zinc-400 leading-relaxed">
                Avant toute ouverture, nous analysons la configuration existante : nature du mur,
                charges reprises, hauteur disponible. Les solutions de renforcement (linteau béton,
                poutre métallique, jambages) sont étudiées selon chaque situation.
              </p>
              <p className="mt-4 text-sm font-light text-zinc-400 leading-relaxed">
                Le chantier est sécurisé et mené étape par étape : étaiement, découpe, mise en place
                du renfort puis reprise de maçonnerie. Vous êtes informé de chaque phase et le chantier
                est rendu propre. Nous intervenons à Bernay, Évreux et dans toute l'Eure.
              </p>
            </div>
            <div className="card-dark p-8 flex gap-4">
              <AlertTriangle size={22} className="text-[#C4A484] shrink-0 mt-1" />
              <p className="text-sm font-light text-zinc-400 leading-relaxed">
                Pour les configurations complexes ou les grandes portées, une étude structurelle par
                un bureau d'études peut être nécessaire : nous vous orientons en toute transparence
                avant le démarrage du chantier.
              </p>
            </div>
          </Reveal>
        </div>

        <div className="max-w-4xl mx-auto px-6 lg:px-10 mt-20">
          <SectionHead overline="Questions fréquentes" title="Vos questions, nos réponses" />
          <FAQAccordion items={faq} testid="faq-ouverture" />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-16">
          <Reveal>
            <p className="text-[10px] tracking-[0.5em] uppercase text-[#C4A484] mb-5">Voir aussi</p>
            <RelatedLinks slugs={["renovation-bernay", "extension-maison", "maconnerie-bernay", "gros-oeuvre"]} />
          </Reveal>
        </div>
      </section>

      <CTABand />
    </>
  );
}
